import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BookPagination from '../../components/BookPagination';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Target, Plus, Edit, Trash2, Search, BookOpen, Settings, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { quizService } from '../../services/quizService';
import { bookService } from '../../services/bookService';
import { authService } from '../../services/authService';
import { questionService } from '../../services/questionService';
import { aiService } from '../../services/aiService';
import { useToast } from '../../hooks/use-toast';

const AdminQuizzes = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quizzes, setQuizzes] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const ADMIN_PAGE_SIZE = 12;
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiConnectionTest, setAiConnectionTest] = useState(null);
  const [aiFormData, setAiFormData] = useState({
    book_title: '',
    book_author: '',
    quiz_title: '',
    difficulty: 'Medium',
    num_questions: 10,
    question_prompt: ''
  });
  const [formData, setFormData] = useState({
    book_id: '',
    title: '',
    description: '',
    difficulty: '',
    time_limit: '',
    questions_count: ''
  });

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchAllBooks();
  }, [navigate]);

  // Debounce search — reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch quizzes whenever page or search changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { skip: (currentPage - 1) * ADMIN_PAGE_SIZE, limit: ADMIN_PAGE_SIZE };
        if (debouncedSearch) params.search = debouncedSearch;
        const countParams = debouncedSearch ? { search: debouncedSearch } : {};
        const [quizzesData, { count }] = await Promise.all([
          quizService.getQuizzes(params),
          quizService.getQuizCount(countParams),
        ]);
        if (!cancelled) {
          setQuizzes(quizzesData);
          setTotalQuizzes(count);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching quizzes:', error);
          toast({ title: "Error", description: "Failed to load quizzes.", variant: "destructive" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentPage, debouncedSearch]);

  const fetchQuizzes = async () => {
    setLoading(true);
    try {
      const params = { skip: (currentPage - 1) * ADMIN_PAGE_SIZE, limit: ADMIN_PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;

      const countParams = debouncedSearch ? { search: debouncedSearch } : {};

      const [quizzesData, { count }] = await Promise.all([
        quizService.getQuizzes(params),
        quizService.getQuizCount(countParams),
      ]);
      setQuizzes(quizzesData);
      setTotalQuizzes(count);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      toast({ title: "Error", description: "Failed to load quizzes.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBooks = async () => {
    try {
      const booksData = await bookService.getBooks({ limit: 1000 });
      setBooks(booksData);
    } catch (error) {
      console.error('Error fetching books for dropdown:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const quizData = {
        book_id: formData.book_id,
        title: formData.title,
        description: formData.description,
        difficulty: formData.difficulty,
        time_limit: parseInt(formData.time_limit),
        questions_count: parseInt(formData.questions_count)
      };

      if (editingQuiz) {
        await quizService.updateQuiz(editingQuiz.id, quizData);
        toast({
          title: "Success",
          description: "Quiz updated successfully!",
        });
      } else {
        await quizService.createQuiz(quizData);
        toast({
          title: "Success",
          description: "Quiz created successfully!",
        });
      }

      resetForm();
      fetchQuizzes();
      
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save quiz.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (quiz) => {
    setFormData({
      book_id: quiz.book_id,
      title: quiz.title,
      description: quiz.description,
      difficulty: quiz.difficulty,
      time_limit: quiz.time_limit.toString(),
      questions_count: quiz.questions_count.toString()
    });
    setEditingQuiz(quiz);
    setShowAddForm(true);
  };

  const handleDelete = async (quiz) => {
    if (!window.confirm(`Are you sure you want to delete "${quiz.title}"? This will also delete all associated questions.`)) {
      return;
    }

    try {
      await quizService.deleteQuiz(quiz.id);
      toast({
        title: "Success",
        description: "Quiz deleted successfully!",
      });
      setCurrentPage(1);
      fetchQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete quiz.",
        variant: "destructive",
      });
    }
  };

  const getBookTitle = (bookId) => {
    const book = books.find(b => b.id === bookId);
    return book ? book.title : 'Unknown Book';
  };

  const testAIConnection = async () => {
    try {
      const result = await aiService.testConnection();
      setAiConnectionTest(result);
      return result.success;
    } catch (error) {
      setAiConnectionTest({
        success: false,
        message: "Failed to connect to AI service"
      });
      return false;
    }
  };

  const handleAIGenerate = async () => {
    if (!aiFormData.book_title.trim() || !aiFormData.quiz_title.trim()) {
      toast({
        title: "Error",
        description: "Please enter book title and quiz title.",
        variant: "destructive",
      });
      return;
    }

    setAiGenerating(true);
    try {
      const generateData = {
        book_title: aiFormData.book_title,
        book_author: aiFormData.book_author || undefined,
        quiz_title: aiFormData.quiz_title,
        difficulty: aiFormData.difficulty,
        num_questions: aiFormData.num_questions,
        question_prompt: aiFormData.question_prompt || undefined
      };

      const result = await aiService.generateQuiz(generateData);
      
      if (result.success) {
        toast({
          title: "Success",
          description: `Generated complete quiz with ${result.questions_created} questions!`,
        });
        
        setShowAIGenerate(false);
        setAiFormData({
          book_title: '',
          book_author: '',
          quiz_title: '',
          difficulty: 'Medium',
          num_questions: 10,
          question_prompt: ''
        });
        fetchQuizzes();
      } else {
        throw new Error(result.message || "Failed to generate quiz");
      }
      
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to generate quiz. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      book_id: '',
      title: '',
      description: '',
      difficulty: '',
      time_limit: '',
      questions_count: ''
    });
    setShowAddForm(false);
    setEditingQuiz(null);
  };

  return (
    <Layout isAdmin={true}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Quiz Management</h1>
                <p className="text-gray-600">Create and manage quizzes for your books</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    setShowAIGenerate(true);
                    await testAIConnection();
                  }}
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Complete Quiz with AI
                </Button>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Quiz
                </Button>
              </div>
            </div>
          </div>

          {/* AI Generate Complete Quiz Modal */}
          {showAIGenerate && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
                  Generate Complete Quiz with Gemini AI
                </CardTitle>
                
                {/* AI Connection Status */}
                {aiConnectionTest && (
                  <div className={`flex items-center space-x-2 text-sm ${
                    aiConnectionTest.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {aiConnectionTest.success ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                    <span>{aiConnectionTest.message}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Complete Quiz Generation:</h4>
                  <p className="text-sm text-purple-800 mb-3">
                    Using Gemini AI, we'll create a complete quiz including the book (if needed), quiz details, 
                    and intelligent questions. The AI will generate book-specific questions based on plot, 
                    characters, and themes.
                  </p>
                  <p className="text-xs text-purple-700">
                    This creates everything in one go: book + quiz + questions!
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="book_title">Book Title *</Label>
                    <Input
                      id="book_title"
                      value={aiFormData.book_title}
                      onChange={(e) => setAiFormData({...aiFormData, book_title: e.target.value})}
                      placeholder="e.g., Harry Potter and the Philosopher's Stone"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="book_author">Book Author (Optional)</Label>
                    <Input
                      id="book_author"
                      value={aiFormData.book_author}
                      onChange={(e) => setAiFormData({...aiFormData, book_author: e.target.value})}
                      placeholder="e.g., J.K. Rowling"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="quiz_title">Quiz Title *</Label>
                    <Input
                      id="quiz_title"
                      value={aiFormData.quiz_title}
                      onChange={(e) => setAiFormData({...aiFormData, quiz_title: e.target.value})}
                      placeholder="e.g., Harry Potter Complete Quiz"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="difficulty">Difficulty Level</Label>
                    <Select value={aiFormData.difficulty} onValueChange={(value) => setAiFormData({...aiFormData, difficulty: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Easy">Easy</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="num_questions">Number of Questions</Label>
                    <Select value={aiFormData.num_questions.toString()} onValueChange={(value) => setAiFormData({...aiFormData, num_questions: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 questions</SelectItem>
                        <SelectItem value="10">10 questions</SelectItem>
                        <SelectItem value="15">15 questions</SelectItem>
                        <SelectItem value="20">20 questions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="question_prompt">Focus Areas (Optional)</Label>
                  <Textarea
                    id="question_prompt"
                    value={aiFormData.question_prompt}
                    onChange={(e) => setAiFormData({...aiFormData, question_prompt: e.target.value})}
                    placeholder="e.g., Focus on main characters, plot twists, and character relationships"
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAIGenerate} 
                    disabled={aiGenerating || (aiConnectionTest && !aiConnectionTest.success)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    {aiGenerating ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    {aiGenerating ? 'Generating Complete Quiz...' : 'Generate Complete Quiz'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowAIGenerate(false);
                      setAiConnectionTest(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                
                {aiConnectionTest && aiConnectionTest.success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>✓ Connected to Gemini AI</strong><br />
                      Ready to generate a complete quiz with intelligent, book-specific questions.
                    </p>
                  </div>
                )}
                
                {aiConnectionTest && !aiConnectionTest.success && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">
                      <strong>⚠ AI Service Issue:</strong><br />
                      {aiConnectionTest.message}. Please check your configuration or try again later.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search quizzes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/80 backdrop-blur-sm border-white/20"
              />
            </div>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle>{editingQuiz ? 'Edit Quiz' : 'Add New Quiz'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="book_id">Book *</Label>
                    <Select value={formData.book_id} onValueChange={(value) => handleSelectChange('book_id', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a book" />
                      </SelectTrigger>
                      <SelectContent>
                        {books.map((book) => (
                          <SelectItem key={book.id} value={book.id}>
                            {book.title} by {book.author}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="title">Quiz Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="difficulty">Difficulty *</Label>
                      <Select value={formData.difficulty} onValueChange={(value) => handleSelectChange('difficulty', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select difficulty" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Easy">Easy</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Hard">Hard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="time_limit">Time Limit (minutes) *</Label>
                      <Input
                        id="time_limit"
                        name="time_limit"
                        type="number"
                        value={formData.time_limit}
                        onChange={handleInputChange}
                        min="1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="questions_count">Expected Questions Count *</Label>
                      <Input
                        id="questions_count"
                        name="questions_count"
                        type="number"
                        value={formData.questions_count}
                        onChange={handleInputChange}
                        min="1"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="submit">
                      {editingQuiz ? 'Update Quiz' : 'Create Quiz'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Quizzes List */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {quizzes.map((quiz) => (
                  <Card key={quiz.id} className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{quiz.title}</h3>
                          <p className="text-sm text-gray-600 mb-3">{getBookTitle(quiz.book_id)}</p>

                          <div className="flex items-center gap-2 mb-3">
                            <Badge className={
                              quiz.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                              quiz.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {quiz.difficulty}
                            </Badge>
                            <Badge variant="outline">
                              {quiz.questions_count} questions
                            </Badge>
                            <Badge variant="outline">
                              {quiz.time_limit} min
                            </Badge>
                          </div>

                          <p className="text-xs text-gray-500 mb-3">{quiz.description}</p>

                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Attempts: {quiz.attempts || 0}</span>
                            <span>Avg Score: {quiz.average_score || 0}%</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Link to={`/admin/questions/${quiz.id}`}>
                          <Button size="sm" variant="outline">
                            <BookOpen className="h-3 w-3 mr-1" />
                            Questions
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(quiz)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(quiz)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <BookPagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalQuizzes / ADMIN_PAGE_SIZE)}
                onPageChange={setCurrentPage}
                totalItems={totalQuizzes}
                pageSize={ADMIN_PAGE_SIZE}
              />
            </>
          )}
          {quizzes.length === 0 && !loading && (
            <div className="text-center py-12">
              <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No quizzes found</h3>
              <p className="text-gray-600 mb-6">Start by creating your first quiz or use AI to generate a complete quiz.</p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={async () => {
                    setShowAIGenerate(true);
                    await testAIConnection();
                  }}
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Complete Quiz with AI
                </Button>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Quiz Manually
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminQuizzes;
